import { HStack, Text, VStack } from "@chakra-ui/react";
import { LocalDate } from "@js-joda/core";
import { useQuery } from "@tanstack/react-query";
import { auth } from "../../../../../core/auth";
import useApi from "../../../../../shared/hooks/useApi";
import type { PatientId } from "../../../../../shared/schema/schema";
import { currencyFormatter } from "../../../../../shared/utils/currency-formatter";
import { dateFormatter } from "../../../../../shared/utils/date-formatter";
import EntityProfileDataTable, {
  type EntityProfileDataTableColumn,
} from "../../../../entityProfileModal/components/EntityProfileDataTable";
import {
  EntityProfileEmptyState,
  EntityProfileErrorState,
  EntityProfileLoadingState,
} from "../../../../entityProfileModal/components/EntityProfileFeedback";
import type { PatientProfileBridgeActions } from "../../../patientProfileModal.actions";
import type { PatientProfilePatient } from "../../../patientProfileModal.types";
import EntityModalButton from "../../../../entityProfileModal/components/EntityModalButton";

type Props = {
  actions: PatientProfileBridgeActions;
  patient?: PatientProfilePatient;
  patientId: PatientId;
  onDone: () => void;
};

type PatientPayRate = {
  baseRate?: string | null;
  createdAt?: string | null;
  daily?: number | null;
  endDate?: string | null;
  hourly?: number | null;
  id?: number;
  isBasedOnCaregiverRate?: boolean | null;
  payCodeName?: string | null;
  payCodeId?: number | null;
  startDate?: string | null;
  type?: "BASE_RATE" | "FIXED_DIFF" | "PERCENTAGE" | null;
  visit?: number | null;
};

type PatientPayRatesResponse = {
  patientDefaultPayRates?: PatientPayRate[];
  patientPayRates?: PatientPayRate[];
};

type PatientPayRateSortKey =
  | "updatedAt"
  | "payCodeName"
  | "rateType"
  | "basedOn"
  | "hourlyRate"
  | "dailyRate"
  | "visitRate"
  | "dateRange";

function formatDate(value: string | null | undefined) {
  return value === null || value === undefined || value === ""
    ? ""
    : dateFormatter.toDate(LocalDate.parse(value.slice(0, 10)));
}

function formatDateRange(row: PatientPayRate) {
  return `${formatDate(row.startDate)} - ${formatDate(row.endDate)}`;
}

function formatRateType(type: PatientPayRate["type"]) {
  switch (type) {
    case "FIXED_DIFF":
      return "Fixed rate raise (+)";
    case "PERCENTAGE":
      return "Percentage rate raise (%)";
    case "BASE_RATE":
      return "Base rate (by weekly template)";
    case null:
    case undefined:
      return "";
  }
}

function formatCents(value: number | null | undefined) {
  return value === null || value === undefined ? "" : currencyFormatter.formatCents(value);
}

function formatRateValue(row: PatientPayRate, value: number | null | undefined) {
  if (value === null || value === undefined) {
    return "";
  }

  if (row.type === "PERCENTAGE") {
    return `${value}%`;
  }

  return row.type === "FIXED_DIFF" ? `+${formatCents(value)}` : formatCents(value);
}

function getBaseRate(row: PatientPayRate) {
  if (row.type === "BASE_RATE") {
    return "-";
  }

  if (row.baseRate) {
    return row.baseRate;
  }

  return row.isBasedOnCaregiverRate ? "Caregiver's rate" : "Base Rate";
}

function getPayCodeName(row: PatientPayRate) {
  return (
    row.payCodeName ??
    (row.payCodeId === undefined || row.payCodeId === null ? "" : String(row.payCodeId))
  );
}

function PayRateTable(props: {
  emptyLabel: string;
  isDefault?: boolean;
  onEdit?: (row: PatientPayRate) => void;
  rows: PatientPayRate[];
}) {
  const columns: Array<EntityProfileDataTableColumn<PatientPayRate, PatientPayRateSortKey>> = [
    ...(props.isDefault
      ? []
      : [
          {
            key: "updatedAt",
            header: "Updated At",
            render: (row: PatientPayRate) => formatDate(row.createdAt),
            sortKey: "updatedAt" as const,
            sortValue: (row: PatientPayRate) => row.createdAt ?? "",
          },
        ]),
    {
      key: "payCodeName",
      header: "Payroll Code Name",
      render: (row) => getPayCodeName(row),
      sortKey: "payCodeName",
      sortValue: (row) => getPayCodeName(row),
    },
    {
      key: "rateType",
      header: "Rate Type",
      render: (row) => formatRateType(row.type ?? (props.isDefault ? "BASE_RATE" : null)),
      sortKey: "rateType",
      sortValue: (row) => formatRateType(row.type ?? (props.isDefault ? "BASE_RATE" : null)),
    },
    ...(props.isDefault
      ? []
      : [
          {
            key: "basedOn",
            header: "Based On",
            render: (row: PatientPayRate) => getBaseRate(row),
            sortKey: "basedOn" as const,
            sortValue: (row: PatientPayRate) => getBaseRate(row),
          },
        ]),
    {
      key: "hourlyRate",
      header: "Hourly Rate",
      render: (row) => formatRateValue(row, row.hourly),
      sortKey: "hourlyRate",
      sortValue: (row) => row.hourly,
    },
    {
      key: "dailyRate",
      header: "Daily Rate",
      render: (row) => formatRateValue(row, row.daily),
      sortKey: "dailyRate",
      sortValue: (row) => row.daily,
    },
    ...(props.isDefault
      ? [
          {
            key: "visitRate",
            header: "Visit Rate",
            render: (row: PatientPayRate) => formatCents(row.visit),
            sortKey: "visitRate" as const,
            sortValue: (row: PatientPayRate) => row.visit,
          },
        ]
      : []),
    {
      key: "dateRange",
      header: "Date Range",
      render: (row) => formatDateRange(row),
      sortKey: "dateRange",
      sortValue: (row) => row.startDate ?? "",
    },
    ...(props.isDefault || props.onEdit === undefined
      ? []
      : [
          {
            key: "action",
            header: "",
            cellProps: { w: "56px" },
            render: (row: PatientPayRate) => (
              <EntityModalButton
                aria-label="Edit pay rate"
                iconClassName="fa fa-pencil"
                kind="orange"
                title="Edit pay rate"
                onClick={(event) => {
                  event.stopPropagation();
                  props.onEdit?.(row);
                }}
              />
            ),
          },
        ]),
  ];

  if (props.rows.length === 0) {
    return <EntityProfileEmptyState>{props.emptyLabel}</EntityProfileEmptyState>;
  }

  return (
    <EntityProfileDataTable
      columns={columns}
      defaultSort={{ key: "dateRange", direction: "desc" }}
      getRowKey={(row, index) =>
        row.id ?? `${row.payCodeId ?? "pay-code"}-${row.startDate ?? index}`
      }
      rows={props.rows}
    />
  );
}

function calculatePatientPayRateRanges(rows: PatientPayRate[]) {
  const rowsByPayCode = new Map<string, Array<PatientPayRate & { originalIndex: number }>>();

  rows.forEach((row, originalIndex) => {
    const startDate = toYmd(row.startDate);
    const endDate = toYmd(row.endDate);

    if (startDate === null || endDate === null) {
      return;
    }

    const groupKey = String(row.payCodeId ?? getPayCodeName(row));
    const group = rowsByPayCode.get(groupKey) ?? [];
    group.push({
      ...row,
      startDate,
      endDate,
      originalIndex,
    });
    rowsByPayCode.set(groupKey, group);
  });

  return Array.from(rowsByPayCode.values()).flatMap(calculateRangesForPayCode);
}

function calculateRangesForPayCode(rows: Array<PatientPayRate & { originalIndex: number }>) {
  const boundaries = new Set<number>();

  rows.forEach((row) => {
    boundaries.add(toDayNumber(row.startDate));
    boundaries.add(toDayNumber(row.endDate) + 1);
  });

  const sortedBoundaries = Array.from(boundaries).sort((first, second) => first - second);
  const ranges: PatientPayRate[] = [];

  for (let index = 0; index < sortedBoundaries.length - 1; index += 1) {
    const startDay = sortedBoundaries[index];
    const endDay = sortedBoundaries[index + 1] - 1;
    const activeRows = rows.filter(
      (row) => toDayNumber(row.startDate) <= startDay && toDayNumber(row.endDate) >= endDay
    );

    if (activeRows.length === 0) {
      continue;
    }

    const newestRow = activeRows.sort(compareNewestPayRate)[activeRows.length - 1];
    const previousRange = ranges[ranges.length - 1];
    const startDate = fromDayNumber(startDay);
    const endDate = fromDayNumber(endDay);

    if (
      previousRange !== undefined &&
      previousRange.id === newestRow.id &&
      toDayNumber(previousRange.endDate) + 1 === startDay
    ) {
      previousRange.endDate = endDate;
      continue;
    }

    ranges.push({
      ...newestRow,
      startDate,
      endDate,
    });
  }

  return ranges;
}

function compareNewestPayRate(
  first: PatientPayRate & { originalIndex: number },
  second: PatientPayRate & { originalIndex: number }
) {
  const createdAtDiff = toCreatedAtMs(first.createdAt) - toCreatedAtMs(second.createdAt);

  return createdAtDiff === 0 ? first.originalIndex - second.originalIndex : createdAtDiff;
}

function toYmd(value: string | null | undefined) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const ymd = String(value).slice(0, 10);

  return /^\d{4}-\d{2}-\d{2}$/.test(ymd) ? ymd : null;
}

function toDayNumber(value: string | null | undefined) {
  const ymd = toYmd(value);

  if (ymd === null) {
    return Number.NaN;
  }

  const [year, month, day] = ymd.split("-").map(Number);

  return Math.floor(Date.UTC(year, month - 1, day) / 86_400_000);
}

function fromDayNumber(dayNumber: number) {
  return new Date(dayNumber * 86_400_000).toISOString().slice(0, 10);
}

function toCreatedAtMs(value: string | null | undefined) {
  if (value === null || value === undefined || value === "") {
    return 0;
  }

  const parsed = Date.parse(value);

  return Number.isNaN(parsed) ? 0 : parsed;
}

export default function PatientProfilePayRatesSection(props: Props) {
  const { api } = useApi();
  const canEdit = auth.isAllowedTo("edit_patient_page_pay_rates");
  const payRatesQuery = useQuery({
    queryKey: ["patientProfileModal", props.patientId, "payRates"],
    queryFn: () =>
      api.anyRequest<PatientPayRatesResponse>(
        "GET",
        "/agencies/:agencyId/agency_members/:agencyMemberId/patients/:patientId/pay_rates",
        { path: { patientId: props.patientId } }
      ),
  });
  const refresh = () => {
    props.onDone();
    void payRatesQuery.refetch();
  };
  const openAddPayRate = () => {
    if (props.patient !== undefined) {
      props.actions.openPatientPayRate({ patient: props.patient, onDone: refresh });
    }
  };
  const openResetPayRates = () => {
    if (props.patient !== undefined) {
      props.actions.openPatientPayRateReset({ patient: props.patient, onDone: refresh });
    }
  };
  const openEditPayRate = (row: PatientPayRate) => {
    if (props.patient !== undefined) {
      props.actions.openPatientPayRate({ patient: props.patient, data: row, onDone: refresh });
    }
  };
  const patientPayRateRows = calculatePatientPayRateRanges(
    payRatesQuery.data?.patientPayRates ?? []
  );

  return (
    <VStack alignItems="stretch" spacing={6}>
      {payRatesQuery.isLoading ? (
        <EntityProfileLoadingState label="Loading pay rates..." />
      ) : payRatesQuery.isError ? (
        <EntityProfileErrorState
          label="Could not get patient pay rates"
          onRetry={() => void payRatesQuery.refetch()}
        />
      ) : (
        <>
          <VStack alignItems="stretch" spacing={3}>
            <Text color="#0f172a" fontSize="20px">
              Default Pay Rates
            </Text>
            <PayRateTable
              isDefault
              emptyLabel="No default pay rates."
              rows={(payRatesQuery.data?.patientDefaultPayRates ?? []).map((row) => ({
                ...row,
                type: "BASE_RATE",
              }))}
            />
          </VStack>

          <VStack alignItems="stretch" spacing={3}>
            <HStack justifyContent="space-between">
              <Text color="#0f172a" fontSize="20px">
                Patient Pay Rates
              </Text>
              {canEdit ? (
                <HStack spacing={2}>
                  <EntityModalButton
                    disabled={props.patient === undefined}
                    iconClassName="fa fa-undo"
                    kind="red"
                    size="sm"
                    onClick={openResetPayRates}
                  >
                    Reset Overrides
                  </EntityModalButton>
                  <EntityModalButton
                    disabled={props.patient === undefined}
                    iconClassName="fa fa-plus"
                    kind="blue"
                    size="sm"
                    onClick={openAddPayRate}
                  >
                    Add patient pay rate
                  </EntityModalButton>
                </HStack>
              ) : null}
            </HStack>
            <PayRateTable
              emptyLabel="No patient pay rates."
              rows={patientPayRateRows}
              onEdit={!canEdit || props.patient === undefined ? undefined : openEditPayRate}
            />
          </VStack>
        </>
      )}
    </VStack>
  );
}
